import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Product, PromoGrupoDef, PromoSeleccion } from '../types';
import styles from './styles/ProductDetailPage.module.css';
import { useCart } from '../context/CartContext';
import { FaCheckCircle, FaLock, FaShoppingCart } from 'react-icons/fa';
import { getProductImageUrls } from '../utils';
import { LABELS, MOSTRAR_CAMPOS_ALIMENTOS } from '../data/brand';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

type PromoDetail = {
    items?: Array<{ id_producto: number; cantidad: number; nombre?: string }>;
    grupos?: PromoGrupoDef[];
};

const isPromo = (product?: Product | null) =>
    !!product && (product.es_promocion === true || product.es_promocion === 1);

const getVariantLabel = (variant: Product, baseProduct?: Product | null) => {
    if (variant.valor_propiedad_1) return variant.valor_propiedad_1;
    if (variant.NombreSimple) return variant.NombreSimple;
    const baseName = baseProduct?.NombresProductos || '';
    const withoutBase = variant.NombresProductos.replace(baseName, '').trim();
    return withoutBase || variant.NombresProductos;
};

const escapeHtml = (value: string) => value.replace(/[&<>'"]/g, character => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    "'": '&#39;',
    '"': '&quot;'
}[character] || character));

const buildPromoSection = (
    product: Product,
    field: 'IngredientesSugeridos' | 'descripcionProducto' | 'SugerenciasCoccion',
    fallback?: string | null
) => {
    if (!isPromo(product) || !product.promo_componentes?.length) return fallback || '';

    const seen = new Set<string>();
    return product.promo_componentes
        .map(component => {
            const value = component[field]?.trim();
            if (!value || seen.has(value)) return '';
            seen.add(value);
            return `<div><strong>${escapeHtml(component.NombresProductos)}</strong><br />${value}</div>`;
        })
        .filter(Boolean)
        .join('');
};

const ProductDetailPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [product, setProduct] = useState<Product | null>(null);
    const [promoDetail, setPromoDetail] = useState<PromoDetail | null>(null);
    const [promoSelections, setPromoSelections] = useState<Record<number, Record<number, number>>>({});
    const [selectionMessage, setSelectionMessage] = useState('');
    const [loading, setLoading] = useState(true);
    const [quantity, setQuantity] = useState(1);
    const [activeImage, setActiveImage] = useState(0);
    const [selectedVariantId, setSelectedVariantId] = useState<number | null>(null);
    const [showCartChoice, setShowCartChoice] = useState(false);
    const { addToCart } = useCart();

    useEffect(() => {
        if (!id) return;
        setLoading(true);
        setPromoDetail(null);
        setPromoSelections({});
        setSelectionMessage('');

        fetch(`${API_URL}/productos/${id}`)
            .then(res => res.json())
            .then(data => {
                setProduct(data);
                setSelectedVariantId(data.idProductos);
                setLoading(false);

                if (isPromo(data)) {
                    fetch(`${API_URL}/admin/promociones/${data.idProductos}`)
                        .then(res => res.ok ? res.json() : null)
                        .then(detail => detail && setPromoDetail(detail))
                        .catch(err => console.error(err));
                }
            })
            .catch(err => {
                console.error(err);
                setLoading(false);
            });
    }, [id]);

    const variants = useMemo(() => product?.variantes || [], [product]);
    const selectedProduct = useMemo(() => {
        if (!product) return null;
        return variants.find(variant => variant.idProductos === selectedVariantId) || product;
    }, [product, selectedVariantId, variants]);

    const images = useMemo(() => getProductImageUrls(selectedProduct || product), [selectedProduct, product]);
    const choiceGroups = promoDetail?.grupos || [];

    useEffect(() => {
        setActiveImage(0);
    }, [selectedVariantId, product?.idProductos]);

    const groupTotal = (groupIndex: number) =>
        Object.values(promoSelections[groupIndex] || {}).reduce((total, count) => total + count, 0);

    const updatePromoSelection = (groupIndex: number, productId: number, delta: number, groupMax: number) => {
        setSelectionMessage('');
        setPromoSelections(current => {
            const group = { ...(current[groupIndex] || {}) };
            const actual = group[productId] || 0;
            const total = Object.values(group).reduce((acc, count) => acc + count, 0);

            if (delta > 0 && total >= groupMax) return current;

            const next = actual + delta;
            if (next <= 0) delete group[productId];
            else group[productId] = next;

            return { ...current, [groupIndex]: group };
        });
    };

    const promoSelectionReady = choiceGroups.every((group, groupIndex) => groupTotal(groupIndex) === group.cantidad_elegir);

    const buildPromoSelections = (): PromoSeleccion[] => {
        const selections: PromoSeleccion[] = [];
        choiceGroups.forEach((group, groupIndex) => {
            Object.entries(promoSelections[groupIndex] || {}).forEach(([productId, cantidad]) => {
                const idProducto = Number(productId);
                const option = group.opciones.find(item => item.id_producto === idProducto);
                if (!option || cantidad <= 0) return;
                selections.push({
                    id_grupo: group.id,
                    grupo: group.nombre,
                    id_producto: idProducto,
                    nombre: option.nombre,
                    cantidad
                });
            });
        });
        return selections;
    };

    const handleAddToCart = () => {
        if (!selectedProduct) return;

        if (selectedProduct.stock_total !== null
            && selectedProduct.stock_total !== undefined
            && selectedProduct.stock_total <= 0) {
            setSelectionMessage('Este producto se encuentra sin stock.');
            return;
        }

        if (choiceGroups.length > 0 && !promoSelectionReady) {
            setSelectionMessage('Completa las opciones de la promocion antes de agregarla al carrito.');
            return;
        }

        const productToAdd: Product = choiceGroups.length > 0
            ? { ...selectedProduct, promo_selecciones: buildPromoSelections() }
            : selectedProduct;

        addToCart(productToAdd, quantity);
        setSelectionMessage('Agregado al carrito.');
        setShowCartChoice(true);
    };

    if (loading) return <div className={styles.loader}>Cargando producto...</div>;
    if (!product || !selectedProduct) return <div>Producto no encontrado</div>;

    const precio = new Intl.NumberFormat('es-AR', {
        style: 'currency',
        currency: 'ARS',
        minimumFractionDigits: 0
    }).format(selectedProduct.Precio);
    const imageUrl = images[Math.min(activeImage, Math.max(images.length - 1, 0))];
    const outOfStock = selectedProduct.stock_total !== null
        && selectedProduct.stock_total !== undefined
        && selectedProduct.stock_total <= 0;
    const ingredients = buildPromoSection(
        product,
        'IngredientesSugeridos',
        selectedProduct.IngredientesSugeridos || product.IngredientesSugeridos
    );
    const benefits = buildPromoSection(
        product,
        'descripcionProducto',
        selectedProduct.descripcionProducto || product.descripcionProducto
    );
    const cookingSuggestions = buildPromoSection(
        product,
        'SugerenciasCoccion',
        selectedProduct.SugerenciasCoccion || product.SugerenciasCoccion
    );

    return (
        <div className={styles['detail-container']}>
            <nav className={styles.breadcrumb}>
                <Link to="/">Inicio</Link> &rsaquo;
                <Link to="/productos">Productos</Link>
                {product.nombre_categoria && (
                    <>
                        {' '}&rsaquo;{' '}
                        <Link to={`/productos?cat=${encodeURIComponent(product.nombre_categoria)}`}>
                            {product.nombre_categoria}
                        </Link>
                    </>
                )}
                {product.nombre_subcategoria && (
                    <>
                        {' '}&rsaquo;{' '}
                        <Link to={`/productos?cat=${encodeURIComponent(product.nombre_categoria || '')}&sub=${encodeURIComponent(product.nombre_subcategoria)}`}>
                            {product.nombre_subcategoria}
                        </Link>
                    </>
                )}
                {' '}&rsaquo; <span className={styles.current}>{selectedProduct.NombresProductos}</span>
            </nav>

            <div className={styles['detail-grid']}>
                <div className={styles['detail-image-section']}>
                    <div className={styles['main-image-placeholder']}>
                        {imageUrl ? (
                            <img loading="lazy" decoding="async" src={imageUrl} alt={selectedProduct.NombresProductos} className={styles['detail-main-img']} />
                        ) : (
                            <span>{selectedProduct.NombresProductos.charAt(0)}</span>
                        )}
                    </div>
                    {images.length > 1 && (
                        <div className={styles['detail-thumbnails']}>
                            {images.map((img, idx) => (
                                <button
                                    key={img}
                                    type="button"
                                    className={`${styles['detail-thumb']} ${idx === activeImage ? styles['detail-thumb-active'] : ''}`}
                                    onClick={() => setActiveImage(idx)}
                                >
                                    <img loading="lazy" decoding="async" src={img} alt={`${selectedProduct.NombresProductos} ${idx + 1}`} />
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                <div className={styles['detail-info-section']}>
                    <h1 className={styles['detail-title']}>{selectedProduct.NombresProductos}</h1>

                    {selectedProduct.marca && <div className={styles['detail-brand']}>Marca: {selectedProduct.marca}</div>}

                    <div className={styles['detail-price-block']}>
                        <span className={styles['price-label']}>Precio final</span>
                        <span className={styles['detail-price']}>{precio}</span>
                    </div>

                    {variants.length > 1 && (
                        <div className={styles['variant-selector']}>
                            <span>Variante</span>
                            <div className={styles['variant-options']}>
                                {variants.map(variant => (
                                    <button
                                        type="button"
                                        key={variant.idProductos}
                                        className={variant.idProductos === selectedProduct.idProductos ? styles.selectedVariant : ''}
                                        onClick={() => setSelectedVariantId(variant.idProductos)}
                                    >
                                        {getVariantLabel(variant, product)}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {choiceGroups.length > 0 && (
                        <div className={styles['promo-choice-panel']}>
                            <h3>Elegi los productos de la promocion</h3>
                            {choiceGroups.map((group, groupIndex) => {
                                const selectedCount = groupTotal(groupIndex);
                                return (
                                    <div className={styles['promo-choice-group']} key={group.id}>
                                        <div className={styles['promo-choice-header']}>
                                            <strong>{group.nombre}</strong>
                                            <span>{selectedCount}/{group.cantidad_elegir}</span>
                                        </div>
                                        <div className={styles['promo-choice-options']}>
                                            {group.opciones.map(option => {
                                                const count = promoSelections[groupIndex]?.[option.id_producto] || 0;
                                                return (
                                                    <div className={styles['promo-option-row']} key={option.id_producto}>
                                                        <span>{option.nombre}</span>
                                                        <div>
                                                            <button type="button" onClick={() => updatePromoSelection(groupIndex, option.id_producto, -1, group.cantidad_elegir)} disabled={count === 0}>-</button>
                                                            <strong>{count}</strong>
                                                            <button type="button" onClick={() => updatePromoSelection(groupIndex, option.id_producto, 1, group.cantidad_elegir)} disabled={selectedCount >= group.cantidad_elegir}>+</button>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    <div className={styles['buy-panel']}>
                        <div className={styles['purchase-controls']}>
                            <div>
                                <label htmlFor="product-quantity">Cantidad</label>
                                <span>Selecciona las unidades</span>
                            </div>
                            <div className={styles['qty-selector']}>
                                <button type="button" onClick={() => setQuantity(q => Math.max(1, q - 1))} aria-label="Restar cantidad">−</button>
                                <input id="product-quantity" type="number" value={quantity} readOnly aria-label="Cantidad" />
                                <button type="button" onClick={() => setQuantity(q => q + 1)} aria-label="Sumar cantidad" disabled={outOfStock}>+</button>
                            </div>
                        </div>

                        {selectionMessage && (
                            <div className={selectionMessage.includes('Agregado') ? styles['success-msg'] : styles['error-msg']}>
                                {selectionMessage}
                            </div>
                        )}

                        <button className={styles['btn-add-cart-large']} onClick={handleAddToCart} disabled={outOfStock}>
                            <FaShoppingCart aria-hidden="true" />
                            {outOfStock ? 'Sin stock' : 'Agregar al carrito'}
                        </button>

                        <div className={styles['trust-badges']}>
                            <div>
                                <FaLock aria-hidden="true" />
                                <span><strong>Compra protegida</strong><small>Tus datos están seguros</small></span>
                            </div>
                            <div>
                                <FaCheckCircle aria-hidden="true" />
                                <span><strong>Pedido confirmado</strong><small>Te avisamos al recibirlo</small></span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className={styles['description-content']}>
                <div className={styles['spec-grid']}>
                    <section className={styles['spec-item']}>
                        <strong>Propiedades</strong>
                        <div dangerouslySetInnerHTML={{ __html: benefits || 'Informacion no disponible.' }} />
                    </section>
                    {/*
                      * Estos dos bloques cambian de titulo segun el rubro: en
                      * bazar no aplican "Ingredientes" ni "coccion". Solo se
                      * muestran si hay contenido cargado, para no dejar
                      * secciones con "Informacion no disponible".
                      */}
                    {(ingredients || MOSTRAR_CAMPOS_ALIMENTOS) && (
                        <section className={styles['spec-item']}>
                            <strong>{LABELS.detalleProducto}</strong>
                            <div dangerouslySetInnerHTML={{ __html: ingredients || 'Informacion no disponible.' }} />
                        </section>
                    )}
                    {(cookingSuggestions || MOSTRAR_CAMPOS_ALIMENTOS) && (
                        <section className={styles['spec-item']}>
                            <strong>{LABELS.detalleSecundario}</strong>
                            <div dangerouslySetInnerHTML={{ __html: cookingSuggestions || 'Informacion no disponible.' }} />
                        </section>
                    )}
                </div>
            </div>

            {showCartChoice && (
                <div className={styles['cart-choice-backdrop']} role="dialog" aria-modal="true" aria-labelledby="cart-choice-title">
                    <div className={styles['cart-choice-modal']}>
                        <h2 id="cart-choice-title">Producto agregado</h2>
                        <p>{selectedProduct.NombresProductos} ya esta en tu carrito.</p>
                        <div className={styles['cart-choice-actions']}>
                            <button type="button" onClick={() => navigate('/checkout')} className={styles['cart-choice-primary']}>
                                Ir al carrito
                            </button>
                            <button type="button" onClick={() => setShowCartChoice(false)} className={styles['cart-choice-secondary']}>
                                Seguir comprando
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProductDetailPage;